/**
 * Strength/speed meters for one model row (AB#669) — five thick dashes each,
 * from the backend's 1-5 ratings. Purely presentational; `null` = unrated, and
 * a model with neither rating (the `openrouter/free` router) shows nothing.
 */

import { Brain, Eye, Gauge } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ModelMeterProps {
  strength?: number | null;
  speed?: number | null;
  vision?: boolean;
  /** Picker rows: no labels, the ratings move into a hover `title`. */
  compact?: boolean;
}

function Dashes({ value }: { value: number }) {
  return (
    <span className="flex gap-[2px]" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className={cn(
            'h-[5px] w-2.5 rounded-[1px]',
            i <= value ? 'bg-foreground/80' : 'bg-foreground/20',
          )}
        />
      ))}
    </span>
  );
}

export function ModelMeter({ strength, speed, vision, compact }: ModelMeterProps) {
  const rated: [string, number, LucideIcon][] = [];
  if (strength != null) rated.push(['Strength', strength, Brain]);
  if (speed != null) rated.push(['Speed', speed, Gauge]);
  if (rated.length === 0) return null;

  return (
    <span
      className="flex shrink-0 items-center gap-2"
      role="img"
      aria-label={rated.map(([label, v]) => `${label} ${v} of 5`).join(', ')}
    >
      {rated.map(([label, value, Icon]) => (
        <span key={label} className="flex items-center gap-1" title={`${label} ${value}/5`}>
          <Icon className="h-3 w-3 shrink-0 text-muted-foreground/70" aria-hidden="true" />
          {!compact && <span className="text-[10px] text-muted-foreground">{label}</span>}
          <Dashes value={value} />
        </span>
      ))}
      {vision && (
        <span title="Understands images" className="flex items-center">
          <Eye className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
        </span>
      )}
    </span>
  );
}
